// A character, place or prop, new or existing, as an item with stages (src/ui/stages). A character has three: 1 Picture
// (the image maker, with the name above it), 2 Face (placing its talking face and picking its face style) and 3 Voice
// (who does the voice). Face waits for the first picture. A place or a prop has only Picture, so its page has no stage
// bar: the name, the image maker and, for an existing one, Delete at the foot.
// A new one opens on Picture; an existing one on its Overview, which says what each stage holds and has Delete at the foot
// (library items ask first). Everything saves as it changes (src/library/drafts.ts), so back and Done just leave.
// A new one is a draft until its first picture arrives, and is thrown away if the page is closed before then. The picture
// is made in the image maker; every picture painted there becomes a version and the look (src/library/paintLook.ts).
// Earlier versions are picked from the maker's "Start from", which makes them the look at once.
import { useState } from "react";
import { viewRect } from "../../engine/camera";
import { pictureFrame } from "../../engine/frame";
import { VOICE_PRESETS, presetOf } from "../../audio/voice/voice";
import { faceInVersions, hasPicture, isSelected, removeVersion, selectVersion, versionsOf } from "../../library/build";
import { changeAsset, deleteAsset, latestAsset, markUsed, refreshRef, dropDraft, getDraft, useAsset, useSaveError } from "../../library/drafts";
import { faceStyleId, faceStyles } from "../../library/faceSets";
import { imageSrc } from "../../library/images";
import { paintLook } from "../../library/paintLook";
import { useLibrary } from "../../library/store";
import type { FaceSpots, Viseme } from "../../model/face";
import { byRecent, type Asset } from "../../model/library";
import { baseVoiceOf } from "../../model/speech";
import type { PaintSource } from "../../model/timeline";
import { AssetView, Body, Confirm, DoneFooter, Icon, Layer, Page, Section, Segmented, Toast, TopBar } from "../common";
import { isRunning, lookJob, useJob } from "../jobs";
import { ImageMaker, type MakerGroup, type MakerMode, type MakerPainted, type MakerRequest, type MakerStart } from "../images/ImageMaker";
import { Placer } from "../placer/Placer";
import { ItemActions, OverviewPeek, OverviewRow, OverviewRows, StagePage, useStageGo, type Stage, type StageAt } from "../stages/StagePage";
import { VoiceSetup } from "./VoiceSetup";
import "./assetForm.css";

type Step = "picture" | "face" | "voice";
const NOUN = { character: "character", background: "place", prop: "prop" } as const;
const KIND = { character: "Character", background: "Place", prop: "Prop" } as const;
const MODE: Record<Asset["kind"], MakerMode> = { character: "character", background: "place", prop: "prop" };
const NAME_EG = { character: "e.g. Dez", background: "e.g. Mum's kitchen", prop: "e.g. Toaster" } as const;
const GROUPS = [["character", "Characters"], ["background", "Places"], ["prop", "Props"]] as const;

// Where a face goes when the picture has none yet: near the top, a fifth of the picture's width across.
const startFace = (w: number, h: number): FaceSpots => {
  const ew = w * 0.2, ey = h * 0.14;
  return { eyes: { x: w / 2, y: ey, w: ew }, brows: { x: w / 2, y: ey - ew * 0.36, w: ew * 1.1 }, mouth: { x: w / 2, y: ey + ew * 0.7, w: ew * 0.55 } };
};
// The one face has no character; it is the picture's own.
const ONLY = "";

// `step`: a stage to open on instead of the usual (a new one opens on Picture, an existing one on its Overview).
export function AssetPage({ assetId, step, onClose }: { assetId: string; step?: StageAt<Step>; onClose: () => void }) {
  const asset = useAsset(assetId);
  const lib = useLibrary();
  const saveError = useSaveError();
  const painting = useJob(lookJob(assetId)) !== null;
  const [at, setAt] = useState<StageAt<Step>>(() => step ?? (getDraft(assetId) ? "picture" : "overview"));
  const [refs, setRefs] = useState<PaintSource[]>([]);
  const [changing, setChanging] = useState<{ id: string; ref: PaintSource } | null>(null); // a picked reference whose own page is open (its pencil)
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  if (!asset) return null;

  const set = (patch: Partial<Asset> | ((a: Asset) => Partial<Asset>)) => changeAsset(assetId, patch);
  const isNew = !!getDraft(assetId);
  const isChar = asset.kind === "character";
  const has = hasPicture(asset);

  // A new one nobody has painted is thrown away. One being painted stays until the picture lands.
  function close() {
    if (getDraft(assetId) && !has && !isRunning(lookJob(assetId))) dropDraft(assetId);
    onClose();
  }
  async function remove() {
    try { await deleteAsset(assetId); onClose(); } catch (e) { setToast((e as Error).message); setDeleting(false); }
  }

  const versions = versionsOf(asset);
  const starts: MakerStart[] = versions.map((v, i) => ({ id: v.id, label: `V${i + 1}`, picture: v.picture, ...(v.face ? { face: v.face } : {}) })).reverse();
  const current = versions.find(v => isSelected(asset, v));
  const groups: MakerGroup[] = GROUPS.map(([kind, title]) => ({
    key: kind, title, kind,
    items: lib.assets.filter(a => a.kind === kind && a.id !== assetId && hasPicture(a)).sort(byRecent).map((a): PaintSource => ({ src: a.picture.src, name: a.name, kind: a.kind })),
  }));

  async function paint({ start, refs: used, text, style, styleId }: MakerRequest): Promise<MakerPainted> {
    markUsed(used);
    const { version, note } = await paintLook(assetId, { how: start ? "changed" : "drawn", text, refs: used, style, styleId, ...(start ? { from: { src: start.picture.src, ...(start.face ? { face: start.face } : {}) } } : {}) });
    const n = versionsOf(latestAsset(assetId)!).length;
    return { start: { id: version.id, label: `V${n}`, picture: version.picture, ...(version.face ? { face: version.face } : {}) }, ...(note ? { note } : {}) };
  }

  // Face needs a picture to put the face on.
  const stages: Stage<Step>[] = [
    { key: "picture", label: "Picture", done: has, working: painting },
    { key: "face", label: "Face", done: !!asset.face, wait: has ? undefined : { reason: "Face opens once V1 has arrived. The face goes on the picture, so there has to be one." } },
    { key: "voice", label: "Voice", done: !!asset.voiceMode },
  ];

  // The name, then the image maker. Characters get their face drawn on the big picture; moving it is the Face stage.
  const pictureStage = (
    <>
      <NameField asset={asset} autoFocus={isNew && !asset.name} onChange={name => set({ name })} />
      <ImageMaker bare onNote={setToast} mode={MODE[asset.kind]} refsTitle="Copy from" draftKey={`asset:${assetId}:words`}
        starts={starts} startId={current?.id ?? null} refs={refs} setRefs={setRefs} groups={groups} paint={paint}
        {...(isChar ? { face: { style: asset.faceStyle } } : {})}
        jobKey={lookJob(assetId)} confirmDelete onDelete={s => set(a => { const r = removeVersion(a, s.id); return { picture: r.picture, face: r.face, versions: r.versions }; })}
        onPick={s => { const v = latestAsset(assetId) && versionsOf(latestAsset(assetId)!).find(x => x.id === s.id); if (v) set(selectVersion(v)); }}
        onEdit={r => { const a = lib.assets.find(x => x.kind === r.kind && x.picture.src === r.src); if (a) setChanging({ id: a.id, ref: r }); }} />
    </>
  );
  const delButton = !isNew && (
    <button className="btn danger wide" onClick={() => setDeleting(true)}><Icon name="trash" /> Delete {asset.name || `this ${NOUN[asset.kind]}`}</button>
  );

  return (
    <>
      <StagePage title={asset.name || `New ${NOUN[asset.kind]}`} sub={KIND[asset.kind]} onBack={close} onDone={close}
        stages={isChar ? stages : stages.slice(0, 1)} at={isChar ? at : "picture"} onAt={setAt} hideBar={!isChar}
        className={(isChar ? at : "picture") === "picture" ? "mk" : undefined}
        toast={saveError ?? toast} clearToast={() => setToast(null)}>
        {at === "overview" && isChar && <CharacterOverview asset={asset} stages={stages} onDelete={() => setDeleting(true)} canDelete={!isNew} />}
        {(at === "picture" || !isChar) && <>{pictureStage}{delButton && <Section>{delButton}</Section>}</>}
        {at === "face" && isChar && <FaceStage asset={asset} onChange={set} />}
        {at === "voice" && isChar && <VoiceContent asset={asset} onChange={set} onToast={setToast} />}
      </StagePage>
      {changing && <AssetPage assetId={changing.id} onClose={() => { setRefs(rs => refreshRef(rs, changing.ref, changing.id)); setChanging(null); }} />}
      {deleting && (
        <Confirm title={`Delete ${asset.name}?`} yes="Delete" danger onYes={() => void remove()} onNo={() => setDeleting(false)}>
          Shots already painted with {asset.name} keep them, but they can't be drawn into new shots. This can't be undone.
        </Confirm>
      )}
    </>
  );
}

function NameField({ asset, autoFocus, onChange }: { asset: Asset; autoFocus: boolean; onChange: (name: string) => void }) {
  return (
    <Section title="Name">
      <input className="input big" value={asset.name} maxLength={40} aria-label="Name" placeholder={NAME_EG[asset.kind]} autoFocus={autoFocus} onChange={e => onChange(e.target.value)} />
    </Section>
  );
}

// What each stage holds now, one row each, then the character's own actions.
function CharacterOverview({ asset, stages, canDelete, onDelete }: { asset: Asset; stages: Stage<Step>[]; canDelete: boolean; onDelete: () => void }) {
  const go = useStageGo<Step>();
  const waiting = (k: Step) => !!stages.find(s => s.key === k)?.wait;
  const has = hasPicture(asset);
  const versions = versionsOf(asset);
  const v = Math.max(1, versions.findIndex(x => isSelected(asset, x)) + 1);
  const drawn = faceStyles().find(s => s.id === faceStyleId(asset.faceStyle))?.name;
  const voice = asset.voiceMode === "ai"
    ? `AI voice · ${baseVoiceOf(asset.speech!.voice)?.name ?? asset.speech!.voice}`
    : `Recorded by you · ${VOICE_PRESETS.find(p => p.id === presetOf(asset.voice!))?.name ?? "your own filter"}`;
  return (
    <>
      {has && <OverviewPeek><AssetView asset={asset} /></OverviewPeek>}
      <NameField asset={asset} autoFocus={false} onChange={name => changeAsset(asset.id, { name })} />
      <OverviewRows>
        <OverviewRow thumb={has ? <img src={imageSrc(asset.picture.src)} alt="" /> : <Icon name="image" size={24} />} title="Picture" onClick={() => go("picture")}
          sub={has ? `V${v} of ${versions.length}${drawn ? ` · ${drawn}` : ""}` : "No picture yet"} />
        <OverviewRow round thumb={<Icon name="face" size={24} />} title="Face" onClick={() => go("face")} waiting={waiting("face")}
          sub={asset.face ? `Placed${drawn ? ` · ${drawn}` : ""}` : "Not placed yet"} />
        <OverviewRow round thumb={<Icon name="voice" size={24} />} title="Voice" onClick={() => go("voice")} sub={voice} />
      </OverviewRows>
      {canDelete && (
        <ItemActions title="This character">
          <button className="btn danger text" onClick={onDelete}><Icon name="trash" /> Delete {asset.name || "this character"}</button>
        </ItemActions>
      )}
    </>
  );
}

// Face: put the character's talking face on its picture by hand and pick its face style. The placing is the shared
// Placer with one face; every move is saved as it is made, on the picture in use and on its version.
export function FaceStage({ asset, onChange }: { asset: Asset; onChange: (p: (a: Asset) => Partial<Asset>) => void }) {
  const pic = asset.picture;
  const face = asset.face ?? startFace(pic.w, pic.h);
  const style = faceStyleId(asset.faceStyle);
  return (
    <Placer picture={pic} faces={[{ char: ONLY, face }]} solo fixedCast hint="" styles={{ value: style, onChange: faceStyle => onChange(() => ({ faceStyle })) }}
      onFaces={f => onChange(a => ({ face: f[0]!.face, versions: faceInVersions(versionsOf(a), pic.src, f[0]!.face) }))}
      draw={({ clock, camera, talk }) => ({ ...pictureFrame(pic, face, { clock, blink: false, mouth: talk?.viseme, emotion: talk ? "happy" : "neutral" }, viewRect(camera), style), base: "#2B2E33" })} />
  );
}

// A character's voice page on its own, opened from elsewhere (the timeline's line page). Changes save by themselves.
export function VoiceEditor({ assetId, onBack }: { assetId: string; onBack: () => void }) {
  const asset = useAsset(assetId);
  const [toast, setToast] = useState<string | null>(null);
  if (!asset) return null;
  return (
    <Layer>
      <Page>
        <TopBar onBack={onBack} title="Voice" sub={asset.name || "New character"} />
        <Body page={asset.voiceMode}><VoiceContent asset={asset} onChange={p => changeAsset(assetId, p)} onToast={setToast} /></Body>
        <DoneFooter onDone={onBack} />
        {toast && <Toast onClick={() => setToast(null)}>{toast}</Toast>}
      </Page>
    </Layer>
  );
}

// Who does the voice, and how it sounds: the Voice stage and the standalone voice page.
function VoiceContent({ asset, onChange, onToast }: { asset: Asset; onChange: (p: Partial<Asset>) => void; onToast: (m: string) => void }) {
  const [mouth, setMouth] = useState<Viseme | null>(null);
  return (
    <>
      {hasPicture(asset) && <div className="peek"><div className="frame-box peek-frame paper"><AssetView asset={asset} mouth={mouth} /></div></div>}
      <Section title="Who does the voice?">
        <Segmented label="Who does the voice" value={asset.voiceMode === "ai" ? "ai" : "record"} onChange={voiceMode => onChange({ voiceMode })}
          options={[["record", <><Icon name="mic" size={18} /> I'll record it</>], ["ai", <><Icon name="sparkle" size={18} /> AI voice</>]]} />
      </Section>
      <VoiceSetup draft={asset} onChange={onChange} onMouth={setMouth} onToast={onToast} />
      <p className="hint">Filters only change how lines sound. Recordings and mouth timing stay as they are, and a new filter changes every line {asset.name || "they"} say{asset.name ? "s" : ""}.</p>
    </>
  );
}
